import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CalendarClock, Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/hooks/useAuth'
import { useOrganizationContext } from '@/contexts/OrganizationContext'
import {
  AvailabilityEditor,
  buildDefaultWeek,
  DAYS,
  type WeekAvailability,
} from '@/components/AvailabilityEditor'

export function ProfileAvailabilitySection() {
  const { user } = useAuth()
  const { currentOrg } = useOrganizationContext()
  const { toast } = useToast()

  const [week, setWeek] = useState<WeekAvailability>(buildDefaultWeek())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (user?.id && currentOrg?.id) loadAvailability()
  }, [user?.id, currentOrg?.id])

  const loadAvailability = async () => {
    if (!user?.id || !currentOrg?.id) return
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('member_availability')
        .select('day_of_week, slots')
        .eq('user_id', user.id)
        .eq('org_id', currentOrg.id)

      if (error) throw error

      const newWeek = buildDefaultWeek()
      for (const row of data || []) {
        const slots = Array.isArray(row.slots) ? row.slots : []
        if (newWeek[row.day_of_week]) {
          newWeek[row.day_of_week] = { enabled: slots.length > 0, slots }
        }
      }
      setWeek(newWeek)
    } catch {
      toast({ title: 'Erro ao carregar disponibilidade', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!user?.id || !currentOrg?.id) return
    setSaving(true)
    try {
      for (const { key } of DAYS) {
        const day = week[key]
        if (day.enabled && day.slots.length > 0) {
          const { error } = await supabase.from('member_availability').upsert(
            { user_id: user.id, org_id: currentOrg.id, day_of_week: key, slots: day.slots },
            { onConflict: 'user_id,org_id,day_of_week' }
          )
          if (error) throw error
        } else {
          const { error } = await supabase
            .from('member_availability')
            .delete()
            .eq('user_id', user.id)
            .eq('org_id', currentOrg.id)
            .eq('day_of_week', key)
          if (error) throw error
        }
      }
      toast({ title: 'Disponibilidade salva!' })
    } catch {
      toast({ title: 'Erro ao salvar disponibilidade', variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  if (!currentOrg) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Disponibilidade para Agendamento
        </CardTitle>
        <CardDescription>
          Defina seus horários disponíveis em <strong>{currentOrg.name}</strong>. Essas informações serão usadas para agendamentos.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            <AvailabilityEditor week={week} onChange={setWeek} />
            <div className="flex justify-end pt-2">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar disponibilidade'
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
